import { useState, useEffect } from 'react'

import _ from 'lodash'
import { styled, css } from 'styled-components'

import { withLabel, withFormValue } from 'peach/decorators'
import { humanizeKey } from 'peach/helpers'
import { useOnMountEffect } from 'peach/hooks'

const StyledList = styled.div`
  border: 1px solid ${(p) => p.theme.border};
  border-radius: 4px;
  background-color: ${(p) => p.theme.background};
  padding: 6px;
`

const ListContent = styled.div`
  border: 1px solid ${(p) => p.theme.border};
`

const StyledItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${(p) => p.theme.border};
  background-color: ${(p) => p.theme.offset};
  padding: 4px;
  font-size: 14px;

  &:last-child {
    border-bottom: none;
  }
`

const Section = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const inputStyles = css`
  border: 1px solid ${(p) => p.theme.border};
  background-color: ${(p) => p.theme.background};
  padding: 4px 8px;
  color: ${(p) => p.theme.labelText};

  &:focus {
    color: ${(p) => p.theme.text};
  }
`

const OrderInput = styled.input`
  ${inputStyles};
  width: 48px;
  text-align: center;
  font-size: 12px;
`

const KeyInput = styled.input`
  ${inputStyles};
  margin-left: 8px;
  width: 100%;
`

const Actions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-top: 6px;
`

const Action = styled.button`
  border: none;
  background-color: transparent;
  cursor: ${(p) => (p.disabled ? 'default' : 'pointer')};
  padding: 2px 4px;
  color: ${(p) => (p.disabled ? p.theme.disabledText : p.theme.linkText)};
  font-size: 14px;
`

const Arrow = styled.button`
  margin-left: 4px;
  border: none;
  background-color: transparent;
  cursor: pointer;
  padding: 2px 6px;
  color: ${(p) => (p.disabled ? p.theme.disabledText : p.theme.linkText)};
  font-size: 14px;

  &:active,
  &:hover {
    background-color: rgb(0 0 0 / 5%);
  }
`

const parseValue = (list) =>
  _.map(list, (item, index) => ({ order: `${index}`, value: item }))

const parseList = (list) => {
  const sorted = _.sortBy(list, (item) => Number.parseFloat(item.order))
  return _.map(sorted, 'value')
}

const ValueWrapper = styled.div`
  display: inline-block;
  padding-left: 8px;
`

const DisplayValue = styled.div``

const KeyValue = styled.div`
  padding-top: 4px;
  color: ${(p) => p.theme.mutedText};
  font-size: 10px;
`

const Value = ({ value }) => (
  <ValueWrapper>
    <DisplayValue>{humanizeKey(value)}</DisplayValue>
    <KeyValue>{value}</KeyValue>
  </ValueWrapper>
)

const OrderableList = ({ value, onChange, defaultValue = [], remove, add }) => {
  // const valueString = JSON.stringify(value)

  const [list, setList] = useState(() => parseValue(value))

  // const listString = JSON.stringify(list)

  useEffect(() => {
    setList(parseValue(value))
  }, [value])

  const reset = () => onChange(defaultValue)

  useOnMountEffect(() => {
    if (!value && defaultValue) reset()
  })

  const onReset = () => {
    setList(parseValue(defaultValue))
    onChange(defaultValue)
  }

  const onApply = () => {
    const newValue = parseList(list)
    setList(parseValue(newValue))
    onChange(newValue)
  }

  const size = _.size(list)

  const canReset =
    !_.isEmpty(defaultValue) && !_.isEqual(list, parseValue(defaultValue))

  const canApply = !_.isEqual(list, parseValue(value))

  const displayItems = _.map(list, (item, i) => {
    const { order, value } = item

    const updateListOrder = (list, order) => {
      const newItem = { order, value }
      const newList = [...list]
      newList[i] = newItem
      return newList
    }

    const onOrderChange = (order) => {
      setList((list) => updateListOrder(list, order))
    }

    const onApplyOrder = (order) => {
      const newList = updateListOrder(list, order)
      const newValue = parseList(newList)
      setList(parseValue(newValue))
      onChange(newValue)
    }

    const onUp = () => onApplyOrder(`${Number.parseFloat(order) - 1.5}`)

    const onDown = () => onApplyOrder(`${Number.parseFloat(order) + 1.5}`)

    const onRemove = () => {
      const newList = _.reject(list, { value })
      const newValue = parseList(newList)
      setList(parseValue(newValue))
      onChange(newValue)
    }
    // symbols = '⬆︎⬇︎⇩⇧×⊕⊖⊗⨂⨁⨀'
    return (
      <StyledItem key={value}>
        <Section>
          <OrderInput
            onChange={(event) => onOrderChange(event.target.value)}
            value={order}
          />
          <Value value={value} />
        </Section>
        <Section>
          <Arrow disabled={canApply || i <= 0} onClick={onUp}>
            ⇧
          </Arrow>
          <Arrow disabled={canApply || i >= size - 1} onClick={onDown}>
            ⇩
          </Arrow>
          {remove && (
            <Arrow disabled={canApply} onClick={onRemove}>
              ⨂
            </Arrow>
          )}
        </Section>
      </StyledItem>
    )
  })

  const [addValueStr, setAddValueStr] = useState('')

  const onAdd = () => {
    const newList = [...list, { order: size, value: addValueStr }]
    const newValue = parseList(newList)
    setList(parseValue(newValue))
    onChange(newValue)
    setAddValueStr('')
  }

  const addItem = add && (
    <StyledItem>
      <Section>
        <OrderInput disabled value={size} />
        <KeyInput
          onChange={(event) => setAddValueStr(event.target.value)}
          placeholder='Add Value'
          value={addValueStr}
        />
      </Section>
      <Section>
        <Arrow disabled={canApply || !addValueStr} onClick={onAdd}>
          ⨁
        </Arrow>
      </Section>
    </StyledItem>
  )

  return (
    <StyledList>
      <ListContent>
        {displayItems}
        {addItem}
      </ListContent>
      <Actions>
        <Action disabled={!canApply} onClick={onApply}>
          Apply
        </Action>
        <Action disabled={!canReset} onClick={onReset}>
          Reset
        </Action>
      </Actions>
    </StyledList>
  )
}

export default withFormValue(withLabel(OrderableList))
